/**
 * Methods for interacting with ToDesktop application's menu item.
 *
 * @remarks
 * This package exposes a number of methods for interacting with ToDesktop
 * application's menu item.

 * @experimental
 * @public
 * @packageDocumentation
 */
import { construct, invoke } from "./invoke.js";
/**
 * Creates a new MenuItem.
 *
 * @returns Identifier for the newly created MenuItem.
 * @public
 */
export async function create(options) {
    return construct({ namespace: "MenuItem", args: [options] });
}
/**
 * The id of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem
 * @public
 */
export async function getId({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "id",
    });
}
/**
 * Sets the id of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem; value: value to set.
 * @public
 */
export async function setId({ ref, value, }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "id",
        value,
    });
}
/**
 * A string indicating the item's visible label.
 *
 * @param options - ref: Reference for the MenuItem
 * @public
 */
export async function getLabel({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "label",
    });
}
/**
 * Sets the label of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem; value: value to set.
 * @public
 */
export async function setLabel({ ref, value, }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "label",
        value,
    });
}
/**
 * A Function that is fired when the MenuItem receives a click event.
 *
 * @param options - ref: Reference for the MenuItem;
 * @public
 */
export async function getClick({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "click",
    });
}
/**
 * Sets the click of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem; value: value to set.
 * @public
 */
export async function setClick({ ref, value, }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "click",
        value,
    });
}
/**
 * A Menu (optional) containing the menu item's submenu, if present.
 *
 * @param options - ref: Reference for the MenuItem;
 * @public
 */
export async function getSubmenu({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "submenu",
    });
}
/**
 * Sets the submenu of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem; value: value to set.
 * @public
 */
export async function setSubmenu({ ref, value, }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "submenu",
        value,
    });
}
/**
 * A string indicating the type of the item. Can be normal, separator, submenu, checkbox or radio.
 *
 * @param options - ref: Reference for the MenuItem;
 * @public
 */
export async function getType({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "type",
    });
}
/**
 * Sets the type of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem; value: value to set.
 * @public
 */
export async function setType({ ref, value, }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "type",
        value,
    });
}
/**
 * A string indicating the type of the item. Can be normal, separator, submenu, checkbox or radio.
 *
 * @param options - ref: Reference for the MenuItem;
 * @public
 */
export async function getRole({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "type",
    });
}
/**
 * Sets the role of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem; value: value to set.
 * @public
 */
export async function setRole({ ref, value, }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "role",
        value,
    });
}
/**
 * An Accelerator (optional) indicating the item's accelerator, if set.
 *
 * @param options - ref: Reference for the MenuItem;
 * @public
 */
export async function getAccelerator({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "accelerator",
    });
}
/**
 * Sets the accelerator of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem; value: value to set.
 * @public
 */
export async function setAccelerator({ ref, value, }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "accelerator",
        value,
    });
}
/**
 * A NativeImage | string (optional) indicating the item's icon, if set.
 *
 * @param options - ref: Reference for the MenuItem;
 * @public
 */
export async function getIcon({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "icon",
    });
}
/**
 * Sets the icon of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem; value: value to set.
 * @public
 */
export async function setIcon({ ref, value, }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "icon",
        value,
    });
}
/**
 * A string indicating the item's sublabel.
 *
 * @param options - ref: Reference for the MenuItem;
 * @public
 */
export async function getSublabel({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "sublabel",
    });
}
/**
 * Sets the sublabel of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem; value: value to set.
 * @public
 */
export async function setSublabel({ ref, value, }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "sublabel",
        value,
    });
}
/**
 * A string indicating the item's hover text.
 *
 * @param options - ref: Reference for the MenuItem;
 * @public
 */
export async function getToolTip({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "toolTip",
    });
}
/**
 * Sets the tooltip of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem; value: value to set.
 * @public
 */
export async function setToolTip({ ref, value, }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "toolTip",
        value,
    });
}
/**
 * A boolean indicating whether the item is enabled, this property can be dynamically changed.
 *
 * @param options - ref: Reference for the MenuItem;
 * @public
 */
export async function getEnabled({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "enabled",
    });
}
/**
 * Sets the enabled of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem; value: value to set.
 * @public
 */
export async function setEnabled({ ref, value, }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "enabled",
        value,
    });
}
/**
 * A boolean indicating whether the item is visible, this property can be dynamically changed.
 *
 * @param options - ref: Reference for the MenuItem;
 * @public
 */
export async function getVisible({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "visible",
    });
}
/**
 * Sets the visible of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem; value: value to set.
 * @public
 */
export async function setVisible({ ref, value, }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "visible",
        value,
    });
}
/**
 * A boolean indicating whether the item is checked, this property can be dynamically changed.
 *
 * @param options - ref: Reference for the MenuItem;
 * @public
 */
export async function getChecked({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "visible",
    });
}
/**
 * Sets the checked of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem; value: value to set.
 * @public
 */
export async function setChecked({ ref, value, }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "checked",
        value,
    });
}
/**
 * A boolean indicating if the accelerator should be registered with the system or just displayed.
 *
 * @param options - ref: Reference for the MenuItem;
 * @public
 */
export async function getRegisterAccelerator({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "registerAccelerator",
    });
}
/**
 * Sets the registerAccelerator of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem; value: value to set.
 * @public
 */
export async function setRegisterAccelerator({ ref, value, }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "registerAccelerator",
        value,
    });
}
/**
 * A SharingItem indicating the item to share when the role is shareMenu. MacOS only.
 *
 * @param options - ref: Reference for the MenuItem;
 * @public
 */
export async function getSharingItem({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "sharingItem",
    });
}
/**
 * Sets the sharingItem of the Menuitem
 *
 * @param options - ref: Reference for the MenuItem; value: value to set.
 * @public
 */
export async function setSharingItem({ ref, value, }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "sharingItem",
        value,
    });
}
/**
 * A number indicating an item's sequential unique id.
 *
 * @param options - ref: Reference for the MenuItem;
 * @public
 */
export async function getCommandId({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "commandId",
    });
}
/**
 * A Menu that the item is a part of.
 *
 * @param options - ref: Reference for the MenuItem;
 * @public
 */
export async function getMenu({ ref }) {
    return await invoke({
        instanceRef: ref,
        namespace: "MenuItem",
        property: "menu",
    });
}
